import {Component, computed, effect, inject, OnDestroy, OnInit, Signal} from '@angular/core';
import {Router} from '@angular/router';
import {Context} from '../../models/context';
import {Household} from '../../models/household';
import {Store} from '../../models/store';
import {Stock} from '../../models/stock';
import {ContextService} from "../../services/context/context-signal.service";
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {AppLogger, AppLoggerService} from '../../core/logging';
import {DialogService} from '../../services/dialog/dialog.service';
import {MealPlanStore} from '../../services/meal-plan/meal-plan-store.service';
import {ConsumptionCheckStore} from '../../services/consumption/consumption-check-store.service';
import {MealPlanEntry} from '../../services/meal-plan/meal-plan.models';
import {addDaysKey, keyToLocalDate} from '../../utils/date-key.util';
import {
  faArrowRight,
  faBarcode,
  faBoxOpen,
  faCalendarDays,
  faCartShopping,
  faShuffle,
  faUtensils,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';

/** One upcoming meal, flattened for the dashboard widget. */
interface UpcomingMeal {
  entry: MealPlanEntry;
  dayKey: string;
  slotLabel: string;
}

/** How many upcoming meals the dashboard widget shows. */
const UPCOMING_MEALS_LIMIT = 5;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {

  private readonly log: AppLogger;

  private readonly consumptionChecks = inject(ConsumptionCheckStore);

  context: Signal<Context> = this.contextService.context;
  household: Household;
  store: Store;

  /**
   * Bumped whenever the context changes (household/store).
   * Used to force child teaser components to reload when only ids change.
   */
  reloadToken = 0;

  result: Stock[];
  isSearching = false;

  /**
   * Next few planned meals in the loaded window ([today, today+6]), flattened
   * in day → slot order. The store loads this window on household availability.
   */
  readonly upcomingMeals = computed<UpcomingMeal[]>(() => {
    const grid = this.mealPlan.grid();
    const slots = this.mealPlan.slots();
    const out: UpcomingMeal[] = [];
    for (const dayKey of this.mealPlan.dayKeys()) {
      const cell = grid.get(dayKey);
      if (!cell) continue;
      for (const slot of slots) {
        for (const entry of cell.get(slot.key) ?? []) {
          out.push({entry, dayKey, slotLabel: slot.label});
          if (out.length >= UPCOMING_MEALS_LIMIT) return out;
        }
      }
    }
    return out;
  });

  readonly mealsLoading = computed(() => this.mealPlan.loading());

  /**
   * Whether the quick-check teaser has anything to render. The row it sits in
   * only splits into thirds when it does — an empty teaser draws nothing, and
   * scan mode should not be left sitting in a third of an empty row.
   */
  readonly hasQuickChecks = computed(() => this.consumptionChecks.openCount() > 0);

  protected readonly faWandMagicSparkles = faWandMagicSparkles;
  protected readonly faBarcode = faBarcode;
  protected readonly faBoxOpen = faBoxOpen;
  protected readonly faCartShopping = faCartShopping;
  protected readonly faUtensils = faUtensils;
  protected readonly faCalendarDays = faCalendarDays;
  protected readonly faArrowRight = faArrowRight;
  protected readonly faShuffle = faShuffle;

  constructor(
    private contextService: ContextService,
    private appLogger: AppLoggerService,
    private pageHeaderService: PageHeaderService,
    private dialog: DialogService,
    private mealPlan: MealPlanStore,
    private router: Router,
  ) {
    this.log = this.appLogger.createForClass(HomeComponent);

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;

      const prevHouseholdId = this.household?.id;
      const prevStoreId = this.store?.id;

      this.household = c.household;

      // If the effective context changed, clear stale state and trigger reloads.
      const nextHouseholdId = this.household?.id;
      const nextStoreId = this.store?.id;
      const contextChanged = prevHouseholdId !== nextHouseholdId || prevStoreId !== nextStoreId;

      if (contextChanged) {
        this.resetViewStateForContextChange();
      }
    });

  }

  ngOnInit(): void {
    this.pageHeaderService.setPageTitle('Home');
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
  }

  /** Open the global dialog assistant primed to author a new recipe. The prefilled
   *  ask is what makes the assistant call `propose_recipe` (and render its preview)
   *  rather than just chatting about a recipe in prose. */
  createRecipeWithAssistant(): void {
    // A from-scratch recipe: don't bias it toward whatever happens to be in stock.
    // Household food preferences still apply (the backend default) — only the
    // dedicated recipe-store "unconstrained" surface would pass preferences: 'ignore'.
    this.dialog.openWith('Erstelle ein neues Rezept für ', {propose_recipe: {pantry: 'ignore'}});
  }

  /** Jump to the add-product wizard. */
  createProduct(): void {
    this.router.navigate(['/products/new']);
  }

  /** Open the full-screen scan (barcode) mode. */
  openScanMode(): void {
    this.router.navigate(['/scan']);
  }

  /** Human date label for the meal widget ("Heute", "Morgen", "Di, 28. Juli"). */
  dayLabel(key: string): string {
    if (key === this.mealPlan.todayKey) return 'Heute';
    if (key === addDaysKey(this.mealPlan.todayKey, 1)) return 'Morgen';
    return keyToLocalDate(key).toLocaleDateString('de-CH', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
    });
  }

  // Return YYYY-MM-DD for today
  now(): string {
    return new Date().toISOString().slice(0, 10);
  }

  // Return YYYY-MM-DD for today + 7 days
  nextWeek(): string {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  }

  private resetViewStateForContextChange(): void {

    this.result = undefined;
    this.isSearching = false;
    this.reloadToken++;

    this.log.debug('Context changed -> reloadToken bumped', {
      reloadToken: this.reloadToken,
      householdId: this.household?.id,
      storeId: this.store?.id,
    });
  }

}